import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, RefreshCw, CheckCircle2, XCircle, LogOut, Search, Trash2, CheckSquare, Square, Zap, Plus } from "lucide-react";

import {
  AUTH_KEY,
  STATUS_TABS,
  fetchRides,
  adminPost,
  adminGet,
  adminDelete,
} from "./dispatcher/utils";
import { LiveMapPanel } from "./dispatcher/LiveMapPanel";
import { InsightsPanel } from "./dispatcher/InsightsPanel";
import { DriversPanel } from "./dispatcher/DriversPanel";
import { CalendarPanel } from "./dispatcher/CalendarPanel";
import { RideCard } from "./dispatcher/RideCard";
import { MoveStatusModal } from "./dispatcher/MoveStatusModal";
import { EditRideModal } from "./dispatcher/EditRideModal";
import { ActionModal, AssignModal, SplitModal, NearestDriversModal } from "./dispatcher/Modals";
import { NewRideModal } from "./dispatcher/NewRideModal";
import { PaymentActionModal } from "./dispatcher/PaymentActionModal";
import { PatientsPanel } from "./dispatcher/PatientsPanel";
import { PatientProfileDrawer } from "./dispatcher/PatientProfileDrawer";
import { CashRouteMap } from "./dispatcher/CashRouteMap";
import { HomecareSweepPanel } from "./dispatcher/HomecareSweepPanel";

/* --------------------------- Auth gate --------------------------- */

function LoginGate({ onAuth }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setLoading(true);
    try {
      await fetchRides(pw, "held");
      localStorage.setItem(AUTH_KEY, pw);
      onAuth(pw);
    } catch (err) {
      toast.error(err.message === "UNAUTHORIZED" ? "Wrong password" : "Could not reach the server");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center bg-brand-bg px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-brand-navy/10 rounded-2xl p-8 shadow-sm" data-testid="dispatcher-login-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-brand-navy text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-brand-green font-semibold">Dispatch</div>
            <h1 className="font-serif text-2xl text-brand-deep leading-tight">Dispatcher Sign-in</h1>
          </div>
        </div>
        <label className="block text-xs font-medium text-brand-deep mb-2">Password</label>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Enter dispatcher password"
          data-testid="dispatcher-password-input"
          className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/15 text-brand-deep"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="dispatcher-login-button"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full transition disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-xs text-brand-muted mt-4">This dashboard shows protected health information. Password rotates monthly.</p>
      </form>
    </div>
  );
}

/* --------------------------- Main page --------------------------- */

export default function Dispatcher() {
  const [auth, setAuth] = useState(() => localStorage.getItem(AUTH_KEY) || "");
  const [activeTab, setActiveTab] = useState("held");
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [autoOnly, setAutoOnly] = useState(false);
  const [showNewRide, setShowNewRide] = useState(false);
  const [profileFor, setProfileFor] = useState(null);

  const load = useCallback(async () => {
    if (!auth) return;
    if (activeTab === "drivers" || activeTab === "map" || activeTab === "insights" || activeTab === "calendar" || activeTab === "patients" || activeTab === "homecare_sweep") { setRides([]); return; }
    setLoading(true);
    try {
      let list;
      if (activeTab === "cash_pickups") {
        const data = await adminGet(auth, "rides/cash-pickups");
        list = data.rides || [];
      } else {
        const data = await fetchRides(auth, activeTab);
        list = data.rides || [];
      }
      // On the "Held (action needed)" tab, also surface every ride flagged
      // action_needed=true (completed rides with balance owed / declined cards
      // / no hold placed). Sort action-needed ones to the top with red border.
      if (activeTab === "held") {
        try {
          const extra = await adminGet(auth, "rides/action-needed");
          const merged = [
            ...(extra.rides || []).filter((r) => r.action_needed),
            ...list.filter((r) => !r.action_needed),
          ];
          const seen = new Set();
          list = merged.filter((r) => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          });
        } catch (e) { /* soft-fail: keep held list only */ }
      }
      setRides(list);
    } catch (err) {
      if (err.message === "UNAUTHORIZED") {
        localStorage.removeItem(AUTH_KEY);
        setAuth("");
        toast.error("Session expired — please sign in again");
      } else {
        toast.error(err.message || "Failed to load rides");
      }
    } finally {
      setLoading(false);
    }
  }, [auth, activeTab]);

  useEffect(() => { load(); }, [load]);

  // Refresh every 30s
  useEffect(() => {
    if (!auth) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [auth, load]);

  const sendInvoiceForRide = async (ride) => {
    const to = window.prompt(
      `Send invoice for ${ride.member_name}?\n\nRecipient email (defaults to patient/facility on file):`,
      ride.member_email || ""
    );
    if (to === null) return;
    try {
      const res = await adminPost(auth, `rides/${ride.id}/invoice`, to ? { email: to } : {});
      toast.success(`Invoice ${res.status} → ${res.email}`);
    } catch (err) {
      toast.error(err.message || "Send failed");
    }
  };

  const archiveRide = async (ride) => {
    if (!window.confirm(`Archive this ride for ${ride.member_name}?\n\nThe ride will be moved to the audit archive (recoverable) and hidden from the live board.`)) return;
    try {
      await adminDelete(auth, `rides/${ride.id}`);
      toast.success("Ride archived — kept in audit log");
      setRides((prev) => prev.filter((r) => r.id !== ride.id));
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(ride.id); return n; });
    } catch (err) {
      toast.error(err.message || "Archive failed");
    }
  };

  const changeHold = async (ride) => {
    const currentDollars = ((ride.hold_amount_cents || 0) / 100).toFixed(2);
    const raw = window.prompt(
      `Change hold amount for ${ride.member_name}?\n\n` +
      `Current hold: $${currentDollars}\n\n` +
      `Enter the NEW hold amount in dollars.\n` +
      `⚠️  This will cancel the current hold and email ${ride.member_email || "the customer"} a new checkout link. The ride will be marked 'pending_payment' until they re-authorize.`,
      currentDollars
    );
    if (raw === null) return;
    const dollars = parseFloat(raw);
    if (Number.isNaN(dollars) || dollars < 1 || dollars > 5000) {
      toast.error("Amount must be between $1 and $5,000");
      return;
    }
    const reason = window.prompt("Reason for the change (shown to the customer in the email — optional):", "") || "";
    try {
      const res = await adminPost(auth, `rides/${ride.id}/change-hold`, {
        hold_amount_cents: Math.round(dollars * 100),
        reason,
        origin_url: window.location.origin,
      });
      toast.success(
        `Hold updated to $${(res.hold_amount_cents / 100).toFixed(2)}` +
        (res.email_sent ? " — customer emailed new checkout link" : " — email failed, share this link manually")
      );
      load();
    } catch (err) {
      toast.error(err.message || "Change hold failed");
    }
  };

  const forceHeld = async (ride) => {
    const confirm = window.confirm(
      `MANUAL OVERRIDE — mark ride as Held?\n\n` +
      `Only use this if you can CLEARLY see the hold in your Stripe Dashboard for ${ride.member_name}.\n\n` +
      `The Stripe webhook that normally does this automatically may have failed. This flips the ride to Held so you can assign a driver.\n\n` +
      `Continue?`
    );
    if (!confirm) return;
    const piId = window.prompt(
      "Optional: paste the Stripe PaymentIntent ID (starts with pi_...) to auto-link the card. Leave blank if you don't have it.",
      ""
    );
    if (piId === null) return; // cancelled
    const note = window.prompt("Optional note (for the audit trail):", "") || "";
    try {
      const res = await adminPost(auth, `rides/${ride.id}/force-held`, {
        stripe_payment_intent_id: piId.trim() || undefined,
        note: note.trim() || undefined,
      });
      if (res.status === "rejected") {
        toast.error(`Stripe rejected — ${res.reason}`);
      } else {
        toast.success("Ride marked as Held — you can now assign a driver");
      }
      load();
    } catch (err) {
      toast.error(err.message || "Force-held failed");
    }
  };


  const approveCash = async (ride) => {
    if (!window.confirm(
      `Approve cash pickup for ${ride.member_name}?\n\n` +
      `Confirm with the customer that they'll have cash ready at ` +
      `${ride.return_trip && ride.return_trip !== "none" ? "the return drop-off" : "the destination"}, then approve.`
    )) return;
    try {
      await adminPost(auth, `rides/${ride.id}/approve-cash`, { approved: true });
      toast.success("Cash pickup approved — driver can now record cash");
      load();
    } catch (err) {
      toast.error(err.message || "Approve cash failed");
    }
  };

  const sendPaymentLink = async (ride) => {
    if (!ride.member_email) {
      return toast.error("Customer has no email on file — can't send payment link");
    }
    const amt = ride.status === "pending_payment"
      ? (ride.hold_amount_cents || 0) / 100
      : (ride.balance_owed_cents || 0) / 100;
    if (!window.confirm(
      `Email ${ride.member_email} a Stripe Checkout link for $${amt.toFixed(2)}?\n\n` +
      (ride.status === "pending_payment"
        ? "This will place the hold and unlock driver assignment."
        : "This will collect the balance owed.")
    )) return;
    try {
      const res = await adminPost(auth, `rides/${ride.id}/send-payment-link`, {
        origin_url: window.location.origin,
      });
      toast.success(res.email_sent
        ? `Payment link emailed to ${ride.member_email}`
        : "Link created — email delivery failed; check the console");
      if (!res.email_sent && res.checkout_url) {
        try { await navigator.clipboard.writeText(res.checkout_url); toast.info("Link copied to clipboard"); } catch { /* ignore */ }
      }
      load();
    } catch (err) {
      toast.error(err.message || "Send payment link failed");
    }
  };

  const restoreNoShow = async (ride) => {
    const fee = ((ride.cancellation_fee_cents || 0) / 100).toFixed(2);
    const msg = `Restore ${ride.member_name} from no-show?\n\n` +
      (ride.cancellation_fee_cents > 0
        ? `A $${fee} refund will be issued to their card (5-10 business days).\n\n`
        : "No fee was charged, so no refund is needed.\n\n") +
      "The ride will return to the live board.";
    if (!window.confirm(msg)) return;
    try {
      const res = await adminPost(auth, `rides/${ride.id}/restore-no-show`);
      toast.success(
        res.refunded_cents
          ? `Ride restored — $${(res.refunded_cents / 100).toFixed(2)} refunded to customer.`
          : "Ride restored — customer notified."
      );
      load();
    } catch (err) {
      toast.error(err.message || "Restore failed");
    }
  };

  const waiveHold = async (ride) => {
    const reason = window.prompt(
      `Waive the card hold on this ride?\n\n${ride.member_name} · ${ride.pickup?.slice(0, 40)}…\n\n` +
      "This will cancel any existing Stripe hold, mark the ride as scheduled with NO card hold, " +
      "and log the reason on the ride's audit log. Type the reason (VIP client, service recovery, etc):",
      ""
    );
    if (!reason || !reason.trim()) return;
    const by = window.prompt("Waived by (your name):", "dispatcher") || "dispatcher";
    try {
      const res = await adminPost(auth, `rides/${ride.id}/waive-hold`, {
        hold_waived_reason: reason.trim(),
        hold_waived_by: by.trim(),
      });
      toast.success(res.hold_released ? "Hold released & waived — ride scheduled" : "Hold waived — ride scheduled");
      load();
    } catch (err) { toast.error(err.message || "Waive failed"); }
  };

  const overrideStatus = (ride) => setModal({ ride, mode: "move_status" });
  const editRide = (ride) => setModal({ ride, mode: "edit_ride" });

  const attachCapturedPi = async (ride) => {
    const piId = window.prompt(
      `Attach a paid Stripe PaymentIntent to ${ride.member_name}'s ride.\n\n` +
      `Paste the PaymentIntent id from Stripe (starts with "pi_"). ` +
      `The ride will be marked COMPLETED and the captured amount recorded.\n\n` +
      `Only use this when Stripe shows a SUCCEEDED (captured) payment for this customer that isn't linked to the ride yet.`,
      ""
    );
    if (piId === null) return; // cancelled
    const trimmed = piId.trim();
    if (!trimmed.startsWith("pi_")) {
      toast.error("PaymentIntent id must start with 'pi_'");
      return;
    }
    const note = window.prompt("Optional note for the audit trail:", "") || "";
    try {
      const res = await adminPost(auth, `rides/${ride.id}/attach-captured-pi`, {
        stripe_payment_intent_id: trimmed,
        note: note.trim() || undefined,
        mark_completed: true,
      });
      toast.success(
        `Attached ${trimmed.slice(0, 12)}… · $${((res.captured_amount_cents || 0) / 100).toFixed(2)} captured` +
        (res.card_last4 ? ` · ${res.card_brand || "card"} •••• ${res.card_last4}` : "") +
        ` · Ride → Completed`
      );
      load();
    } catch (err) {
      toast.error(err.message || "Attach failed");
    }
  };

  const resendConfirmation = async (ride) => {
    if (!window.confirm(`Email ride confirmation to ${ride.member_email}?`)) return;
    try {
      const res = await adminPost(auth, `rides/${ride.id}/resend-confirmation`, {});
      toast.success(`Confirmation sent to ${res.email}`);
    } catch (err) { toast.error(err.message || "Email failed"); }
  };

  const filteredRides = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = rides;
    if (autoOnly) list = list.filter((r) => r.auto_assigned);
    if (!q) return list;
    return list.filter((r) => {
      const hay = [
        r.member_name, r.member_phone, r.member_email, r.id,
        r.pickup, r.dropoff, r.driver_name, r.notes, r.special_needs,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rides, searchTerm, autoOnly]);

  const autoRoutedCount = useMemo(() => rides.filter((r) => r.auto_assigned).length, [rides]);

  const toggleSelect = (ride, isChecked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isChecked) next.add(ride.id);
      else next.delete(ride.id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(filteredRides.map((r) => r.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error("Select at least one ride"); return; }
    if (!window.confirm(`Archive ${ids.length} ride${ids.length === 1 ? "" : "s"}?\n\nEach will be moved to the audit archive (recoverable).`)) return;
    const reason = window.prompt("Reason for archiving (optional):", "dispatcher_cleanup") || "dispatcher_cleanup";
    try {
      const res = await adminPost(auth, "rides/bulk-delete", { ride_ids: ids, reason });
      toast.success(`${res.archived} ride${res.archived === 1 ? "" : "s"} archived`);
      setRides((prev) => prev.filter((r) => !selectedIds.has(r.id)));
      clearSelection();
      setBulkMode(false);
    } catch (err) {
      toast.error(err.message || "Bulk archive failed");
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem(AUTH_KEY);
    setAuth("");
    setRides([]);
  };

  if (!auth) return <LoginGate onAuth={setAuth} />;

  const allVisibleSelected = filteredRides.length > 0 && filteredRides.every((r) => selectedIds.has(r.id));

  return (
    <div className="min-h-[calc(100vh-160px)] bg-brand-bg pb-16">
      {/* Header */}
      <div className="bg-brand-deep text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-emerald-300 font-semibold">Dispatch</div>
            <h1 className="font-serif text-3xl md:text-4xl mt-1">Ride Command Center</h1>
            <p className="text-sm text-white/80 mt-1">Complete rides · capture holds · cancel with a custom fee</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNewRide(true)} data-testid="new-ride-button" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-full transition">
              <Plus className="w-4 h-4" /> New ride
            </button>
            <button onClick={load} disabled={loading} data-testid="refresh-rides-button" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full transition disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <a href="/manuals/dispatcher.html" target="_blank" rel="noopener" data-testid="manuals-link" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full transition">
              📘 Manual
            </a>
            <button onClick={handleSignOut} data-testid="signout-button" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm px-4 py-2 rounded-full transition">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 mt-8">
        <div className="flex flex-wrap gap-2 mb-6" role="tablist">
          {STATUS_TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setActiveTab(t.key); setSearchTerm(""); clearSelection(); setBulkMode(false); setAutoOnly(false); }}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${active ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-deep border-brand-navy/15 hover:border-brand-navy"}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Rides list / drivers panel / live map */}
        {activeTab === "map" ? (
          <LiveMapPanel auth={auth} />
        ) : activeTab === "drivers" ? (
          <DriversPanel auth={auth} />
        ) : activeTab === "calendar" ? (
          <CalendarPanel auth={auth} />
        ) : activeTab === "insights" ? (
          <InsightsPanel auth={auth} />
        ) : activeTab === "patients" ? (
          <PatientsPanel auth={auth} />
        ) : activeTab === "homecare_sweep" ? (
          <HomecareSweepPanel auth={auth} />
        ) : loading && rides.length === 0 ? (
          <div className="text-center py-16 text-brand-muted text-sm">Loading rides…</div>
        ) : rides.length === 0 ? (
          <>
            {activeTab === "cash_pickups" && (
              <div className="mb-4">
                <CashRouteMap rides={rides} auth={auth} />
              </div>
            )}
            <div className="bg-white border border-brand-navy/10 rounded-2xl p-12 text-center">
              <div className="w-14 h-14 rounded-full bg-brand-surfaceAlt mx-auto flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="font-serif text-2xl text-brand-deep">Nothing here right now</h3>
              <p className="text-sm text-brand-muted mt-2">No rides in this tab. Try another status filter or click refresh.</p>
            </div>
          </>
        ) : (
          <>
            {activeTab === "cash_pickups" && (
              <div className="mb-4">
                <CashRouteMap rides={rides} auth={auth} />
              </div>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-xl">
                <Search className="w-4 h-4 text-brand-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by patient name, phone, driver, pickup, dropoff, ID…"
                  data-testid="rides-search-input"
                  className="w-full bg-white border border-brand-navy/15 rounded-full pl-10 pr-10 py-2.5 text-sm text-brand-deep placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    data-testid="rides-search-clear"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-deep"
                    aria-label="Clear search"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="text-xs text-brand-muted" data-testid="rides-count">
                Showing <strong className="text-brand-deep">{filteredRides.length}</strong> of {rides.length} ride{rides.length === 1 ? "" : "s"}
              </div>
              <button
                onClick={() => { setBulkMode((v) => !v); clearSelection(); }}
                data-testid="bulk-mode-toggle"
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full border transition ${bulkMode ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-deep border-brand-navy/15 hover:border-brand-navy"}`}
              >
                <CheckSquare className="w-3.5 h-3.5" /> {bulkMode ? "Cancel bulk" : "Select multiple"}
              </button>
              <button
                onClick={() => setAutoOnly((v) => !v)}
                data-testid="auto-only-toggle"
                title={autoRoutedCount ? `${autoRoutedCount} auto-routed ride${autoRoutedCount === 1 ? "" : "s"} in this tab` : "No auto-routed rides in this tab"}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full border transition ${autoOnly ? "bg-teal-600 text-white border-teal-600" : "bg-white text-brand-deep border-brand-navy/15 hover:border-teal-600 hover:text-teal-700"}`}
              >
                <Zap className="w-3.5 h-3.5" /> Auto-routed only{autoRoutedCount ? ` · ${autoRoutedCount}` : ""}
              </button>
            </div>

            {bulkMode && (
              <div className="mb-3 flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="bulk-toolbar">
                <button
                  onClick={allVisibleSelected ? clearSelection : selectAllVisible}
                  data-testid="bulk-select-all"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-deep hover:text-brand-navy"
                >
                  {allVisibleSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  {allVisibleSelected ? "Unselect all" : `Select all ${filteredRides.length}`}
                </button>
                <span className="text-xs text-brand-muted" data-testid="bulk-selected-count">
                  {selectedIds.size} selected
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={bulkArchive}
                    disabled={selectedIds.size === 0}
                    data-testid="bulk-archive-button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Archive {selectedIds.size} ride{selectedIds.size === 1 ? "" : "s"}
                  </button>
                </div>
              </div>
            )}

            {filteredRides.length === 0 ? (
              <div className="bg-white border border-brand-navy/10 rounded-2xl p-8 text-center" data-testid="rides-no-match">
                <p className="text-sm text-brand-muted">No rides match &ldquo;<strong className="text-brand-deep">{searchTerm}</strong>&rdquo;. Try a different search.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="rides-list">
                {filteredRides.map((r) => (
                  <RideCard
                    key={r.id}
                    ride={r}
                    selectable={bulkMode}
                    selected={selectedIds.has(r.id)}
                    onSelect={toggleSelect}
                    onComplete={(ride) => setModal({ ride, mode: "complete" })}
                    onCancel={(ride) => setModal({ ride, mode: "cancel" })}
                    onAssign={(ride) => setModal({ ride, mode: "assign" })}
                    onNearest={(ride) => setModal({ ride, mode: "nearest" })}
                    onSplit={(ride) => setModal({ ride, mode: "split" })}
                    onInvoice={sendInvoiceForRide}
                    onDelete={archiveRide}
                    onChangeHold={changeHold}
                    onForceHeld={forceHeld}
                    onRestoreNoShow={restoreNoShow}
                    onPaymentAction={(ride) => setModal({ ride, mode: "payment_action" })}
                    onSendPaymentLink={sendPaymentLink}
                    onOpenPatient={(pid) => setProfileFor(pid)}
                    onApproveCash={approveCash}
                    onWaiveHold={waiveHold}
                    onOverrideStatus={overrideStatus}
                    onAttachCapturedPi={attachCapturedPi}
                    onResendConfirmation={resendConfirmation}
                    onEdit={editRide}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {modal && modal.mode === "assign" && (
        <AssignModal ride={modal.ride} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} auth={auth} />
      )}
      {modal && modal.mode === "nearest" && (
        <NearestDriversModal ride={modal.ride} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} auth={auth} />
      )}
      {modal && modal.mode === "split" && (
        <SplitModal ride={modal.ride} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} auth={auth} />
      )}
      {modal && modal.mode === "payment_action" && (
        <PaymentActionModal
          ride={modal.ride}
          auth={auth}
          onClose={() => setModal(null)}
          onSaved={() => load()}
        />
      )}
      {modal && modal.mode === "move_status" && (
        <MoveStatusModal ride={modal.ride} auth={auth}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
          onOpenEdit={(r) => setModal({ ride: r, mode: "edit_ride" })} />
      )}
      {modal && modal.mode === "edit_ride" && (
        <EditRideModal ride={modal.ride} auth={auth}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }} />
      )}
      {profileFor && (
        <PatientProfileDrawer
          patientId={profileFor}
          auth={auth}
          onClose={() => setProfileFor(null)}
        />
      )}
      {modal && !["assign", "nearest", "split", "payment_action", "move_status", "edit_ride"].includes(modal.mode) && (
        <ActionModal ride={modal.ride} mode={modal.mode} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} auth={auth} />
      )}
      {showNewRide && (
        <NewRideModal
          auth={auth}
          onClose={() => setShowNewRide(false)}
          onCreated={() => { setShowNewRide(false); load(); }}
        />
      )}
    </div>
  );
}
